/**
 * PublicQuestionnairePage
 * -----------------------
 * This module implements the public-facing questionnaire completion flow, addressed via
 * a URL token. It supports both starting a new submission and resuming an existing draft
 * submission using a locally persisted submission token.
 *
 * Conceptual model
 * ----------------
 * Questionnaire:
 *   A named collection of ordered questions rendered to the user. Each question includes
 *   a type and may define constraints such as "required" and "identity field" semantics.
 *
 * Submission:
 *   A server-side record that captures user answers over time. Submissions can be in a
 *   draft state (resumable) or a submitted state (finalized and no longer editable).
 *
 * Submission token:
 *   A server-issued identifier used to authenticate and associate answer writes for the
 *   public flow. This token is stored in localStorage for draft resume semantics.
 *
 * Identity fields:
 *   Certain questions can be marked as identity fields. Their values are extracted into a
 *   separate identity buffer that is included at submission time as temporary identity
 *   metadata. This allows collection of identity information without requiring an account
 *   login during the public flow.
 *
 * Persistence and resume strategy
 * -------------------------------
 * Initialization attempts to resume a draft submission if a stored submission token exists.
 * When resuming, answers are restored from the submission detail response. If the stored
 * token is invalid, it is cleared and a new submission is started.
 *
 * Answer changes are auto-saved to reduce data loss in the presence of navigation, refresh,
 * or network interruptions. Auto-save is opportunistic; failures are logged and do not
 * block continued interaction.
 *
 * Validation strategy
 * -------------------
 * On final submission, required questions are validated at the client layer by checking
 * the presence of an entry in the answers map. This validation is a user-experience
 * improvement and does not replace server-side validation.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Text, Paper, Button, CircularProgress, Card, CardContent, LinearProgress } from '../../ui';
import {
  getPublicQuestionnaire,
  startPublicSubmission,
  saveAnswerByToken,
  submitByToken,
  getSubmissionByToken,
  type PublicQuestionnaire,
  type Question,
  type AnswerCreate,
  type SubmissionDetail,
} from '../../api/questionnaire';
import { QuestionInput } from './PublicQuestionnairePage.QuestionInput';
import { useToast } from '../../contexts/useToast';
import { extractErrorMessage } from '../../utils/errorMessage';
import {
  PublicQuestionnaireFullPageLoading,
  PublicQuestionnaireFullPageError,
  PublicQuestionnaireSubmittedView,
} from './PublicQuestionnairePage.Views';

/**
 * AnswerState is a normalized client-side representation of per-question answers.
 * The structure is keyed by question identifier to allow direct updates and constant-time
 * lookup during rendering and validation.
 *
 * Each stored answer uses optional fields because the question type determines which
 * answer property is relevant, and incomplete drafts should not force non-applicable keys.
 */
interface AnswerState {
  [questionId: number]: {
    valueText?: string;
    valueNumber?: number;
    valueDate?: string;
    valueBoolean?: boolean;
    valueOptionCode?: string;
  };
}

/** Keyed by the route token, so different questionnaires do not collide in localStorage. */
const draftKey = (token: string) => `questionnaire_${token}`;

/**
 * Every start is a new submission, and StrictMode runs the opening effect twice, so two starts
 * asked at once for the same public link share one request.
 */
const startsInFlight = new Map<string, Promise<string>>();

/** Starts a submission and caches its token for draft resume as soon as it is issued. */
function startDraft(token: string): Promise<string> {
  const pending = startsInFlight.get(token);
  if (pending) return pending;

  const started = startPublicSubmission(token)
    .then(({ submissionToken }) => {
      localStorage.setItem(draftKey(token), submissionToken);
      return submissionToken;
    })
    .finally(() => startsInFlight.delete(token));
  startsInFlight.set(token, started);
  return started;
}

export default function PublicQuestionnairePage() {
  const { t } = useTranslation();
  const toast = useToast();
  // Answers save on every keystroke, so a failing save is said once, not once per key.
  const answerSaveFailing = useRef(false);

  /**
   * The route token is the public entry point identifier. It is distinct from the submission
   * token used for subsequent save/submit operations.
   */
  const { token } = useParams<{ token: string }>();

  /**
   * The questionnaire definition is read from its public link, which creates nothing.
   * Rendering is deferred until this definition is available.
   */
  const [questionnaire, setQuestionnaire] = useState<PublicQuestionnaire | null>(null);

  /**
   * Submission details are maintained for potential future use, such as richer resume behavior
   * or status-dependent UI. The current implementation stores it but does not render it.
   */
  const [_submission, setSubmission] = useState<SubmissionDetail | null>(null);

  /**
   * The submission token authorizes draft writes and final submission. It is cached in
   * localStorage to allow resuming drafts across browser sessions.
   */
  const [submissionToken, setSubmissionToken] = useState<string | null>(null);

  /**
   * Loading and submitting are separated to provide accurate UI interlocks for initialization
   * and final submission.
   */
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  /**
   * Error is used for blocking initialization failures (for example, invalid links or missing
   * resources). Non-blocking errors are surfaced via snackbar where appropriate.
   */
  const [error, setError] = useState<string | null>(null);

  /**
   * Step-based navigation is currently unused in the UI but retained as a placeholder for
   * future multi-step questionnaires or pagination.
   */
  const [_currentStep, _setCurrentStep] = useState(0);

  /**
   * Answers are stored as a draft map that is updated immediately on input changes.
   * When a submission token exists, updates are also persisted via the auto-save endpoint.
   */
  const [answers, setAnswers] = useState<AnswerState>({});

  /**
   * Identity data is extracted from designated identity questions. These values are submitted
   * as temporary identity attributes to support public submissions without requiring login.
   *
   * Date of birth is stored as a Date instance for formatting and conversion convenience.
   */
  const [identityData, setIdentityData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    dateOfBirth: null as Date | null,
  });

  /**
   * Submitted state switches the page into a terminal acknowledgement view. The isNewUser
   * flag allows the UI to provide contextual follow-up messaging when applicable.
   */
  const [submitted, setSubmitted] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);

  /**
   * Initializes the public questionnaire submission.
   *
   * Control flow:
   * - Rejects missing route token as an invalid link.
   * - Attempts to resume an existing draft submission using a cached submission token.
   * - Falls back to starting a new submission if no resumable draft exists.
   * - Reads the questionnaire definition from its public link either way.
   *
   * Resume semantics:
   * A cached token is considered valid only if it resolves to a Draft submission. Submitted
   * submissions transition directly to the terminal acknowledgement view. Invalid cached
   * tokens are cleared to prevent repeated resume failures.
   */
  const initializeSubmission = useCallback(async () => {
    if (!token) {
      setError(t('publicQuestionnairePage.invalidLink'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const savedToken = localStorage.getItem(draftKey(token));

      if (savedToken) {
        let existingSubmission: SubmissionDetail | null = null;
        try {
          existingSubmission = await getSubmissionByToken(savedToken);
        } catch {
          /**
           * Any failure to resume is interpreted as an invalid cached token. The token is removed so
           * that subsequent initialization attempts proceed directly to a new submission.
           */
          localStorage.removeItem(draftKey(token));
        }

        if (existingSubmission?.status === 'Draft') {
          setSubmission(existingSubmission);
          setSubmissionToken(savedToken);

          /**
           * Answers are restored into the local draft map to repopulate UI controls. Each answer is
           * represented as a sparse structure to avoid writing irrelevant fields for a question type.
           */
          const restoredAnswers: AnswerState = {};
          existingSubmission.answers.forEach((answer) => {
            restoredAnswers[answer.questionId] = {
              valueText: answer.valueText ?? undefined,
              valueNumber: answer.valueNumber ?? undefined,
              valueDate: answer.valueDate ?? undefined,
              valueBoolean: answer.valueBoolean ?? undefined,
              valueOptionCode: answer.valueOptionCode ?? undefined,
            };
          });
          setAnswers(restoredAnswers);

          setQuestionnaire(await getPublicQuestionnaire(token));
          return;
        } else if (existingSubmission?.status === 'Submitted') {
          /**
           * A submitted submission is treated as terminal. Draft state is not restored in order to
           * prevent accidental re-entry into an already finalized submission.
           */
          setSubmitted(true);
          return;
        }
      }

      const [definition, startedToken] = await Promise.all([getPublicQuestionnaire(token), startDraft(token)]);
      setQuestionnaire(definition);
      setSubmissionToken(startedToken);
      setError(null);
    } catch (err) {
      setError(t('publicQuestionnairePage.notFound'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    initializeSubmission();
  }, [initializeSubmission]);

  /**
   * Updates a single question answer in local state and opportunistically persists it.
   *
   * Persistence:
   * When a submission token is available, the answer is sent to the backend immediately
   * as a draft save. Failures are logged but do not block further UI interaction.
   *
   * Identity extraction:
   * For identity-designated questions, the value is also projected into `identityData`.
   * These derived values are later included in the final submission payload.
   */
  const handleAnswerChange = async (question: Question, value: Partial<AnswerCreate>) => {
    setAnswers((prev) => ({
      ...prev,
      [question.id]: {
        ...prev[question.id],
        ...value,
      },
    }));

    if (submissionToken) {
      try {
        /**
         * The payload intentionally includes the questionId for clarity and to conform with typical
         * backend contracts for answer creation.
         */
        const answerData: AnswerCreate = {
          questionId: question.id,
          ...value,
        };
        await saveAnswerByToken(submissionToken, answerData);
        answerSaveFailing.current = false;
      } catch (err) {
        if (!answerSaveFailing.current) {
          toast.error(extractErrorMessage(err, t('publicQuestionnairePage.answerSaveFailed')));
        }
        answerSaveFailing.current = true;
      }
    }

    if (question.isIdentityField && question.identityFieldType) {
      const identityUpdate: Partial<typeof identityData> = {};

      /**
       * The mapping between identity field types and local state keys is explicit. This provides a
       * clear correspondence between questionnaire configuration and submission metadata fields.
       */
      switch (question.identityFieldType) {
        case 'email':
          identityUpdate.email = value.valueText || '';
          break;
        case 'first_name':
          identityUpdate.firstName = value.valueText || '';
          break;
        case 'last_name':
          identityUpdate.lastName = value.valueText || '';
          break;
        case 'date_of_birth':
          if (value.valueDate) {
            identityUpdate.dateOfBirth = new Date(value.valueDate);
          }
          break;
      }

      setIdentityData((prev) => ({ ...prev, ...identityUpdate }));
    }
  };

  /**
   * Finalizes the submission.
   *
   * Validation:
   * Required fields are checked by confirming that an answer object exists for each required
   * question. This is a conservative client-side validation strategy intended to reduce
   * preventable submission errors.
   *
   * Payload construction:
   * Answers are transformed from the internal map representation into a list. Identity data
   * is supplied as temporary metadata fields so the backend can associate the submission with
   * user identity where supported.
   */
  const handleSubmit = async () => {
    if (!token || !submissionToken || !questionnaire) return;

    const missingRequired = questionnaire.questions.filter((q) => q.isRequired && !answers[q.id]);

    if (missingRequired.length > 0) {
      toast.error(t('publicQuestionnairePage.missingRequired', { count: missingRequired.length }));
      return;
    }

    try {
      setSubmitting(true);

      const answersList: AnswerCreate[] = Object.entries(answers).map(([questionId, answer]) => ({
        questionId: parseInt(questionId),
        ...answer,
      }));

      /**
       * Date of birth is normalized to an ISO yyyy-mm-dd string to match common backend date
       * representations that are timezone-agnostic.
       */
      const result = await submitByToken(submissionToken, {
        answers: answersList,
        tempEmail: identityData.email || undefined,
        tempFirstName: identityData.firstName || undefined,
        tempLastName: identityData.lastName || undefined,
        tempDateOfBirth: identityData.dateOfBirth
          ? identityData.dateOfBirth.toISOString().split('T')[0]
          : undefined,
      });

      setSubmitted(true);
      setIsNewUser(result.isNewUser);

      /**
       * The cached draft token is removed after successful submission to prevent unintended resume.
       */
      localStorage.removeItem(draftKey(token));
    } catch (err) {
      toast.error(t('publicQuestionnairePage.submitError'));
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Returns the number of questions that have any non-empty value recorded.
   *
   * Semantics:
   * An answer is considered present when at least one stored field is neither undefined nor
   * an empty string. This aligns with the UI's representation of answered status.
   */
  const getAnsweredCount = () => {
    if (!questionnaire) return 0;
    return questionnaire.questions.filter((q) => {
      const answer = answers[q.id];
      return answer && Object.values(answer).some((v) => v !== undefined && v !== '');
    }).length;
  };

  /**
   * Loading state is full-page to avoid presenting incomplete or inconsistent UI while
   * initialization and resume logic is in progress.
   */
  if (loading) {
    return <PublicQuestionnaireFullPageLoading />;
  }

  /**
   * Error state is full-page because initialization failures typically imply the user cannot
   * proceed (for example, invalid token links). Non-blocking errors are handled separately
   * through snackbars.
   */
  if (error) {
    return <PublicQuestionnaireFullPageError error={error} checkLinkText={t('publicQuestionnairePage.checkLink')} />;
  }

  /**
   * Submitted state is terminal: it confirms completion and optionally reports whether a new
   * user record was created as a side effect of submission.
   */
  if (submitted) {
    return <PublicQuestionnaireSubmittedView isNewUser={isNewUser} t={t} />;
  }

  /**
   * Defensive guard: rendering without a questionnaire definition would produce an unusable UI.
   * This condition should be rare, as initialization typically either sets an error or loads data.
   */
  if (!questionnaire) {
    return null;
  }

  /**
   * Progress is computed as a percentage of answered questions. This measure reflects completion
   * broadly but does not distinguish partial answers within multi-field question models.
   */
  const progress = (getAnsweredCount() / questionnaire.questions.length) * 100;

  return (
    <Box styles={{ backgroundColor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Box styles={{ maxWidth: 800, mx: 'auto', px: 2 }}>
        <Paper styles={{ p: 3, mb: 3 }}>
          <Text level="h4" gutterBottom>
            {questionnaire.name}
          </Text>

          {questionnaire.description && (
            <Text level="body1" tone="text.secondary" styles={{ mb: 2 }}>
              {questionnaire.description}
            </Text>
          )}

          <Box>
            <Text level="body2" tone="text.secondary" styles={{ mb: 1 }}>
              {t('publicQuestionnairePage.progress')}: {getAnsweredCount()} / {questionnaire.questions.length}{' '}
              {t('publicQuestionnairePage.questions')}
            </Text>
            <LinearProgress variant="determinate" value={progress} styles={{ height: 8, borderRadius: 4 }} />
          </Box>
        </Paper>

        <Box styles={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {questionnaire.questions.map((question, index) => (
            <Card key={question.id} emphasis='outline'>
              <CardContent>
                <Box styles={{ alignItems: 'flex-start', display: 'flex', gap: 2, mb: 2 }}>
                  <Avatar
                    styles={{
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      width: 28,
                      height: 28,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    {index + 1}
                  </Avatar>

                  <Box styles={{ flex: 1 }}>
                    <Text level="body1" styles={{ fontWeight: 'medium', mb: 0.5 }}>
                      {question.prompt}
                      {question.isRequired && (
                        <Text as="span" tone="error.main" styles={{ ml: 0.5 }}>
                          *
                        </Text>
                      )}
                    </Text>

                    {question.isIdentityField && (
                      <Text level="caption" tone="info.main">
                        {t('publicQuestionnairePage.identityFieldInfo')}
                      </Text>
                    )}
                  </Box>
                </Box>

                <QuestionInput
                  question={question}
                  currentAnswer={answers[question.id] || {}}
                  handleAnswerChange={handleAnswerChange}
                  t={t}
                />
              </CardContent>
            </Card>
          ))}
        </Box>

        <Paper styles={{ p: 3, mt: 3, textAlign: 'center' }}>
          <Button
            emphasis='solid'
            size='lg'
            onClick={handleSubmit}
            disabled={submitting}
            styles={{ minWidth: 200 }}
          >
            {submitting ? <CircularProgress size={24} /> : t('publicQuestionnairePage.submitButton')}
          </Button>

          <Text level="caption"  tone="text.secondary" styles={{ display: 'block', mt: 1 }}>
            {t('publicQuestionnairePage.autoSave')}
          </Text>
        </Paper>
      </Box>

    </Box>
  );
}
